import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

export default function AdminParticipantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/participants/${id}/attempts`).then((res) => setData(res.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-20 text-center text-slate-500">Loading...</div>;
  if (!data) return null;

  const { participant, attempts } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 space-y-6">
        <button onClick={() => navigate("/admin/dashboard")} data-testid={ADMIN.backButton} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-denso-red">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <div className="bg-white rounded-md border border-slate-200 p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">{participant.name} {participant.surname}</h1>
            <p className="text-sm text-slate-500">{participant.branch} — {participant.province} — {participant.mobile}</p>
          </div>
          <Badge className={participant.certified ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
            {participant.certified ? `Certified (${participant.certificate_id})` : "Not Certified"}
          </Badge>
        </div>

        <div className="space-y-4">
          <h2 className="font-semibold text-slate-800">Quiz Attempts ({attempts.length})</h2>
          {attempts.length === 0 && <p className="text-sm text-slate-400">No completed attempts yet.</p>}
          {attempts.map((a) => (
            <div key={a.id} data-testid={ADMIN.attemptCard(a.id)} className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-semibold text-slate-800">Attempt #{a.attempt_number} (Cycle {a.cycle_number})</span>
                  <span className="text-slate-500">Score: {a.score}/{a.total_questions} ({a.percentage}%)</span>
                  <span className="text-slate-500">Time: {a.time_taken_seconds}s</span>
                  <span className="text-slate-500">{new Date(a.completed_at).toLocaleString()}</span>
                </div>
                <Badge className={a.passed ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}>
                  {a.passed ? "Passed" : "Failed"}
                </Badge>
              </div>
              {!a.passed && (
                <div className="space-y-2 border-t border-slate-100 pt-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Incorrect Answers</p>
                  {a.answers.filter((ans) => !ans.is_correct).map((ans) => (
                    <div key={ans.question_id} className="text-sm bg-red-50/50 border border-red-100 rounded-md p-3">
                      <p className="font-medium text-slate-800 flex items-start gap-1.5">
                        <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" /> Q{ans.question_id}. {ans.question_text}
                      </p>
                      <p className="text-slate-600 mt-1 ml-5">
                        Answered: {ans.selected_option ? ans.options[ans.selected_option] : "No answer"} — Correct: <span className="text-emerald-700 font-medium">{ans.options[ans.correct_option]}</span>
                      </p>
                    </div>
                  ))}
                  {a.answers.filter((ans) => !ans.is_correct).length === 0 && (
                    <p className="text-sm text-emerald-600 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> All answers correct</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
